<template>
  <div class="key-issues-section">
    <h4>Key Issues</h4>
    <div class="key-issues-tabs">
      <Button type="button" @click="$emit('update:activeTab', 'revenue')" :class="['key-issues-btn', { active: activeTab === 'revenue' }]">Revenue Impact</Button>
      <Button type="button" @click="$emit('update:activeTab', 'budgetary')" :class="['key-issues-btn', { active: activeTab === 'budgetary' }]">Budgetary Impact</Button>
      <Button type="button" @click="$emit('update:activeTab', 'dof')" :class="['key-issues-btn', { active: activeTab === 'dof' }]">Other DOF Concerns</Button>
    </div>
    <div class="key-issues-content">
      <div v-show="activeTab === 'revenue'" class="key-issues-panel">
        <div v-for="(row, idx) in (keyIssues && keyIssues.revenue_rows) || []" :key="idx" class="impact-card">
          <div class="impact-card-header">
            <h5>Revenue Impact #{{ idx + 1 }}</h5>
            <Button type="button" icon="pi pi-trash" severity="danger" size="small" text @click="$emit('deleteRevenueRow', idx)" :disabled="readOnly"/>
          </div>
          <div class="impact-card-fields">
            <div class="form-group">
              <label>Current / Existing Regime</label>
              <Textarea v-model="row.current_existing_regime" rows="2" placeholder="Enter current/existing regime" :disabled="readOnly" />
            </div>
            <div class="form-group">
              <label>Proposal / Salient Provisions</label>
              <div class="no-estimate-check">
                <Checkbox v-model="row.no_estimate" :binary="true" inputId="no_est_rev_{{idx}}" :disabled="readOnly" />
                <label :for="'no_est_rev_' + idx" class="no-estimate-label">No Estimate</label>
              </div>
              <div v-if="!row.no_estimate" class="year-range-controls">
                <div class="form-group">
                  <label>Start Year:</label>
                  <DatePicker v-model="localRevenueStartYearDate" view="year" dateFormat="yy" @date-select="updateRevenueStartYear" :disabled="readOnly" />
                </div>
                <div class="form-group">
                  <label>End Year:</label>
                  <DatePicker v-model="localRevenueEndYearDate" view="year" dateFormat="yy" @date-select="updateRevenueEndYear" :disabled="readOnly" />
                </div>
              </div>
              <div v-if="!row.no_estimate" class="table-container">
                <table class="revenue-table">
                  <thead>
                    <tr>
                      <th class="particulars-col">Particulars</th>
                      <th v-for="year in revenueYearRange" :key="year">{{ year }}</th>
                      <th class="delete-col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(pRow, pIdx) in (row.particulars_rows || [{ particulars: row.particulars || '' }])" :key="pIdx">
                      <td class="particulars-col">
                        <Textarea v-model="pRow.particulars" placeholder="Enter particulars" rows="2" :disabled="readOnly" />
                      </td>
                      <td v-for="year in revenueYearRange" :key="year">
                        <InputNumber v-model="pRow[year]" :min-fraction-digits="2" :max-fraction-digits="2" placeholder="0.00" :class="['revenue-input', { 'negative-value': pRow[year] < 0 }]" :use-grouping="true" mode="currency" currency="PHP" locale="en-PH" :disabled="readOnly"/>
                      </td>
                      <td class="delete-col">
                        <Button v-if="(row.particulars_rows || []).length > 1" type="button" icon="pi pi-trash" severity="danger" size="small" text @click="row.particulars_rows.splice(pIdx, 1)" :disabled="readOnly" />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <button type="button" @click="addParticularsRow(row)" class="add-particulars-btn" :disabled="readOnly">+ Add Particulars Row</button>
              </div>
            </div>
            <div class="form-group">
              <label>FPMG Recommendation Position</label>
              <Textarea v-model="row.fpmg_recommendation_positions" rows="2" placeholder="Enter FPMG recommendation" :disabled="readOnly" />
            </div>
            <div class="form-group">
              <label>OSEC Instruction / Remarks</label>
              <Textarea v-model="row.osec_instruction_remarks" rows="2" placeholder="Enter OSEC instruction/remarks" :disabled="readOnly" />
            </div>
          </div>
        </div>

        <button type="button" @click="$emit('addRevenueRow')" class="add-row-btn" :disabled="readOnly">+ Add Revenue Impact</button>
      </div>

      <div v-show="activeTab === 'budgetary'" class="key-issues-panel">
        <div v-for="(row, idx) in (keyIssues && keyIssues.budgetary_rows) || []" :key="idx" class="impact-card">
          <div class="impact-card-header">
            <h5>Budgetary Impact #{{ idx + 1 }}</h5>
            <Button type="button" icon="pi pi-trash" severity="danger" size="small" text @click="$emit('deleteBudgetaryRow', idx)" :disabled="readOnly"/>
          </div>
          <div class="impact-card-fields">
            <div class="form-group">
              <label>Current / Existing Regime</label>
              <Textarea v-model="row.current_existing_regime" rows="2" placeholder="Enter current/existing regime" :disabled="readOnly" />
            </div>
            <div class="form-group">
              <label>Proposal / Salient Provisions</label>
              <div class="no-estimate-check">
                <Checkbox v-model="row.no_estimate" :binary="true" :inputId="'no_est_bud_' + idx" :disabled="readOnly" />
                <label :for="'no_est_bud_' + idx" class="no-estimate-label">No Estimate</label>
              </div>
              <div v-if="!row.no_estimate" class="year-range-controls">
                <div class="form-group">
                  <label>Start Year:</label>
                  <DatePicker v-model="localBudgetaryStartYearDate" view="year" dateFormat="yy" @date-select="updateBudgetaryStartYear" :disabled="readOnly" />
                </div>
                <div class="form-group">
                  <label>End Year:</label>
                  <DatePicker v-model="localBudgetaryEndYearDate" view="year" dateFormat="yy" @date-select="updateBudgetaryEndYear" :disabled="readOnly" />
                </div>
              </div>
              <div v-if="!row.no_estimate" class="table-container">
                <table class="revenue-table">
                  <thead>
                    <tr>
                      <th class="particulars-col">Particulars</th>
                      <th v-for="year in budgetaryYearRange" :key="year">{{ year }}</th>
                      <th class="delete-col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(pRow, pIdx) in (row.particulars_rows || [{ particulars: row.particulars || '' }])" :key="pIdx">
                      <td class="particulars-col">
                        <Textarea v-model="pRow.particulars" placeholder="Enter particulars" rows="2" :disabled="readOnly" />
                      </td>
                      <td v-for="year in budgetaryYearRange" :key="year">
                        <InputNumber v-model="pRow[year]" :min-fraction-digits="2" :max-fraction-digits="2" placeholder="0.00" :class="['revenue-input', { 'negative-value': pRow[year] < 0 }]" :use-grouping="true" mode="currency" currency="PHP" locale="en-PH" :disabled="readOnly"/>
                      </td>
                      <td class="delete-col">
                        <Button v-if="(row.particulars_rows || []).length > 1" type="button" icon="pi pi-trash" severity="danger" size="small" text @click="row.particulars_rows.splice(pIdx, 1)" :disabled="readOnly" />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <button type="button" @click="addParticularsRow(row)" class="add-particulars-btn" :disabled="readOnly">+ Add Particulars Row</button>
              </div>
            </div>
            <div class="form-group">
              <label>FPMG Recommendation Position</label>
              <Textarea v-model="row.fpmg_recommendation_positions" rows="2" placeholder="Enter FPMG recommendation" :disabled="readOnly" />
            </div>
            <div class="form-group">
              <label>OSEC Instruction / Remarks</label>
              <Textarea v-model="row.osec_instruction_remarks" rows="2" placeholder="Enter OSEC instruction/remarks" :disabled="readOnly" />
            </div>
          </div>
        </div>

        <button type="button" @click="$emit('addBudgetaryRow')" class="add-row-btn" :disabled="readOnly">+ Add Budgetary Impact</button>
      </div>
      <div v-show="activeTab === 'dof'" class="key-issues-panel">
        <div v-for="(row, idx) in (keyIssues && keyIssues.dof_concerns_rows) || []" :key="idx" class="impact-card">
          <div class="impact-card-header">
            <h5>DOF Concern #{{ idx + 1 }}</h5>
            <Button type="button" icon="pi pi-trash" severity="danger" size="small" text @click="$emit('deleteDofConcernsRow', idx)" :disabled="readOnly"/>
          </div>
          <div class="impact-card-fields">
            <div class="form-group">
              <label>Current / Existing Regime</label>
              <Textarea v-model="row.current_existing_regime" rows="2" placeholder="Enter current/existing regime" :disabled="readOnly" />
            </div>
            <div class="form-group">
              <label>Proposal / Salient Provisions</label>
              <Textarea v-model="row.proposal_salient_provisions" rows="2" placeholder="Enter proposal/salient provisions" :disabled="readOnly" />
            </div>
            <div class="form-group">
              <label>FPMG Recommendation Position</label>
              <Textarea v-model="row.fpmg_recommendation_positions" rows="2" placeholder="Enter FPMG recommendation" :disabled="readOnly" />
            </div>
            <div class="form-group">
              <label>OSEC Instruction / Remarks</label>
              <Textarea v-model="row.osec_instruction_remarks" rows="2" placeholder="Enter OSEC instruction/remarks" :disabled="readOnly" />
            </div>
          </div>
        </div>
        <button type="button" @click="$emit('addDofConcernsRow')" class="add-row-btn" :disabled="readOnly">+ Add DOF Concern</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import InputNumber from 'primevue/inputnumber'
import Textarea from 'primevue/textarea'
import DatePicker from 'primevue/datepicker'
import Checkbox from 'primevue/checkbox'

const props = defineProps({
  keyIssues: {
    type: Object,
    required: true
  },
  activeTab: {
    type: String,
    default: 'revenue'
  },
  revenueStartYear: {
    type: Number,
    default: () => new Date().getFullYear()
  },
  revenueEndYear: {
    type: Number,
    default: () => new Date().getFullYear() + 5
  },
  budgetaryStartYear: {
    type: Number,
    default: () => new Date().getFullYear()
  },
  budgetaryEndYear: {
    type: Number,
    default: () => new Date().getFullYear() + 5
  },
  readOnly: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits([
  'update:activeTab',
  'addRevenueRow',
  'addBudgetaryRow',
  'addDofConcernsRow',
  'deleteRevenueRow',
  'deleteBudgetaryRow',
  'deleteDofConcernsRow',
  'getYearTotal',
  'autoResize',
  'updateRevenueStartYear',
  'updateRevenueEndYear',
  'updateBudgetaryStartYear',
  'updateBudgetaryEndYear'
])

const addParticularsRow = (row) => {
  if (!row.particulars_rows) {
    row.particulars_rows = [{ particulars: row.particulars || '' }]
  }
  row.particulars_rows.push({ particulars: '' })
}

const localRevenueStartYearDate = ref(null)
const localRevenueEndYearDate = ref(null)
const localBudgetaryStartYearDate = ref(null)
const localBudgetaryEndYearDate = ref(null)
const localRevenueStartYear = ref(null)
const localRevenueEndYear = ref(null)
const localBudgetaryStartYear = ref(null)
const localBudgetaryEndYear = ref(null)
const getMaxYearFromData = (rows) => {
  if (!rows || !Array.isArray(rows) || rows.length === 0) return new Date().getFullYear()
  let maxYear = new Date().getFullYear()
  rows.forEach(row => {
    Object.keys(row).forEach(key => {
      const year = parseInt(key)
      if (!isNaN(year) && year > maxYear) {
        maxYear = year
      }
    })
  })
  return maxYear
}

watch(() => props.revenueStartYear, (newVal) => {
  if (!isNaN(newVal)) {
    localRevenueStartYear.value = newVal
    localRevenueStartYearDate.value = new Date(newVal, 0, 1)
  }
}, { immediate: true })

watch(() => props.revenueEndYear, (newVal) => {
  const maxYear = getMaxYearFromData(props.keyIssues?.revenue_rows)
  const year = Math.max(isNaN(newVal) ? new Date().getFullYear() + 5 : newVal, maxYear)
  localRevenueEndYear.value = year
  localRevenueEndYearDate.value = new Date(year, 0, 1)
}, { immediate: true })

watch(() => props.budgetaryStartYear, (newVal) => {
  if (!isNaN(newVal)) {
    localBudgetaryStartYear.value = newVal
    localBudgetaryStartYearDate.value = new Date(newVal, 0, 1)
  }
}, { immediate: true })

watch(() => props.budgetaryEndYear, (newVal) => {
  const maxYear = getMaxYearFromData(props.keyIssues?.budgetary_rows)
  const year = Math.max(isNaN(newVal) ? new Date().getFullYear() + 5 : newVal, maxYear)
  localBudgetaryEndYear.value = year
  localBudgetaryEndYearDate.value = new Date(year, 0, 1)
}, { immediate: true })

watch(() => props.keyIssues, () => {
  const revenueMaxYear = getMaxYearFromData(props.keyIssues?.revenue_rows)
  const budgetaryMaxYear = getMaxYearFromData(props.keyIssues?.budgetary_rows)
  
  const revenueYear = Math.max(isNaN(props.revenueEndYear) ? new Date().getFullYear() + 5 : props.revenueEndYear, revenueMaxYear)
  const budgetaryYear = Math.max(isNaN(props.budgetaryEndYear) ? new Date().getFullYear() + 5 : props.budgetaryEndYear, budgetaryMaxYear)
  
  localRevenueEndYear.value = revenueYear
  localBudgetaryEndYear.value = budgetaryYear
  localRevenueEndYearDate.value = new Date(revenueYear, 0, 1)
  localBudgetaryEndYearDate.value = new Date(budgetaryYear, 0, 1)
}, { deep: true, immediate: true })

const revenueYearRange = computed(() => {
  const startYear = localRevenueStartYear.value || props.revenueStartYear || new Date().getFullYear()
  const maxYear = getMaxYearFromData(props.keyIssues?.revenue_rows)
  const endYear = Math.max(localRevenueEndYear.value || props.revenueEndYear || new Date().getFullYear() + 5, maxYear)
  const years = []
  for (let year = startYear; year <= endYear; year++) {
    years.push(year)
  }
  return years
})

const budgetaryYearRange = computed(() => {
  const startYear = localBudgetaryStartYear.value || props.budgetaryStartYear || new Date().getFullYear()
  const maxYear = getMaxYearFromData(props.keyIssues?.budgetary_rows)
  const endYear = Math.max(localBudgetaryEndYear.value || props.budgetaryEndYear || new Date().getFullYear() + 5, maxYear)
  const years = []
  for (let year = startYear; year <= endYear; year++) {
    years.push(year)
  }
  return years
})
const updateRevenueStartYear = (date) => {
  localRevenueStartYear.value = date.getFullYear()
  emit('updateRevenueStartYear', date.getFullYear())
}

const updateRevenueEndYear = (date) => {
  localRevenueEndYear.value = date.getFullYear()
  emit('updateRevenueEndYear', date.getFullYear())
}

const updateBudgetaryStartYear = (date) => {
  localBudgetaryStartYear.value = date.getFullYear()
  emit('updateBudgetaryStartYear', date.getFullYear())
}

const updateBudgetaryEndYear = (date) => {
  localBudgetaryEndYear.value = date.getFullYear()
  emit('updateBudgetaryEndYear', date.getFullYear())
}

const getYearTotal = (rows, year) => {
  if (!rows || !Array.isArray(rows)) return '₱0.00'
  const total = rows.reduce((sum, row) => {
    const value = parseFloat(row[year]) || 0
    return sum + value
  }, 0)
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(total)
}
</script>

<style scoped>
.key-issues-section {
  border-radius: 12px;
  /* padding: 1.5rem; */
  /* border: 1px solid #e5e7eb; */
 
  /* margin-top: 2rem; */
}

.key-issues-section h4 {
  color: #92400e;
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 3px solid #f59e0b;
}

.key-issues-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.key-issues-btn {
  background: #f3f4f6;
  border: 2px solid #e5e7eb;
  color: #374151;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.85rem;
}

.key-issues-btn:hover {
  background: #e5e7eb;
  border-color: #d1d5db;
}

.key-issues-btn.active {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  color: white;
  border-color: #f59e0b;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.key-issues-content {
  /* background: white;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e5e7eb; */
}

.key-issues-panel {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.year-range-controls {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.year-range-controls .form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.year-range-controls label {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.year-range-controls input {
  padding: 0.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  width: 100px;
}

.table-container {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-bottom: 1rem;
}

.revenue-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.revenue-table th {
  /* background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%); */
  color: black;
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
  border-bottom: 2px solid #f59e0b;
}

.revenue-table td {
  padding: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
}

.particulars-col {
  min-width: 200px;
  width: 30%;
}

.delete-col {
  width: 60px;
  text-align: center;
}

.revenue-table td:not(.particulars-col):not(.delete-col) {
  min-width: 120px;
  text-align: center;
}

.revenue-table textarea {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 0.5rem;
  font-size: 0.85rem;
  resize: vertical;
  min-height: 60px;
}

.revenue-table textarea:focus {
  outline: none;
  border-color: #f59e0b;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.1);
}

.revenue-input {
  width: 100%;
  text-align: right;
}

.revenue-input :deep(.p-inputnumber-input) {
  text-align: right;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.85rem;
}

.revenue-input :deep(.p-inputnumber-input:focus) {
  outline: none;
  border-color: #f59e0b;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.1);
}

/* Target negative values in InputNumber */
.negative-value :deep(.p-inputnumber-input) {
  color: #dc2626 !important;
}

.negative-value :deep(.p-inputnumber-input:focus) {
  color: #dc2626 !important;
  border-color: #dc2626;
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.1);
}

.total-row {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  font-weight: 700;
}

.total-label {
  color: #92400e;
  font-weight: 700;
  text-align: center;
}

.total-cell {
  color: #92400e;
  font-weight: 700;
  text-align: right;
  padding: 0.75rem;
}

.add-row-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.add-row-btn:hover {
  background: linear-gradient(135deg, #047857 0%, #10b981 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.delete-row-btn {
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.3s;
  margin: 0 auto;
}

.delete-row-btn:hover {
  background: #b91c1c;
  transform: scale(1.1);
}

@media (max-width: 768px) {
  .key-issues-tabs {
    flex-direction: column;
  }
  
  .year-range-controls {
    flex-direction: column;
  }
  
  .year-range-controls input {
    width: 100%;
  }
  
  .revenue-table {
    font-size: 0.8rem;
  }
  
  .revenue-table th,
  .revenue-table td {
    padding: 0.5rem 0.25rem;
  }
  
  .particulars-col {
    min-width: 150px;
  }
  
  .revenue-table td:not(.particulars-col) {
    min-width: 80px;
  }
}

/* Impact Card Styles */
.impact-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  background: white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}

.impact-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 1rem;
}

.impact-card-header h5 {
  color: #1e3a8a;
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
}

.impact-card-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.impact-card-fields .form-group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.impact-card-fields .form-group label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.impact-card-fields :deep(.p-textarea) {
  width: 100% !important;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  transition: border-color 0.2s;
}

.impact-card-fields :deep(.p-textarea:focus) {
  border-color: #1e3a8a;
  box-shadow: 0 0 0 2px rgba(30, 58, 138, 0.1);
}

.impact-card-fields :deep(.p-textarea:disabled) {
  background: #f8fafc;
  color: #64748b;
}

.no-estimate-check {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.no-estimate-label {
  font-size: 0.85rem !important;
  font-weight: 500 !important;
  color: #374151 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  cursor: pointer;
}

.add-particulars-btn {
  background: #6b7280;
  border: none;
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  margin-top: 0.5rem;
  float: right;
  transition: all 0.2s;
}

.add-particulars-btn:hover {
  background: #4b5563;
}
</style>